import React, { useEffect, useState } from "react";
import { Input } from "components/Graphs/Input";
import { Button } from "components/Graphs/Button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "components/Graphs/Tabs";
import { Server, Globe, Info, LockKeyhole, Plus, Trash2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/Graphs/Select";
import { FlatCard } from "components/UI-lib";
import type { RuntimeType } from "../types";
import { ContainerSelected } from "components/Environment/shared/BuildTypes";
import { Tooltip as MuiTooltip } from "@mui/material";
import { Switch } from "components/Graphs/Switch";
import { MAX_CTA_COUNT } from "components/Environment/Create/ExposePorts";
import { validateTunnelName } from "components/utils";
import { MicroK8sNodePortNotice } from "components/Environment/shared/MicroK8sNodePortNotice";
import {
  checkNameConflicts,
  checkPortConflicts,
  isValidPortFormat,
  isValidPortNumber,
  type FirewallRule,
  type NetworkService,
} from "../networkServices";

interface NetworkAccessConfigurationProps {
  services: NetworkService[];
  onServicesChange: (services: NetworkService[]) => void;
  runtimeType: RuntimeType;
  container: ContainerSelected;
  firewallRules?: FirewallRule[];
  onFirewallRulesChange?: (rules: FirewallRule[]) => void;
  configurationError?: string;
}

const NetworkAccessConfiguration: React.FC<NetworkAccessConfigurationProps> = ({
  services,
  onServicesChange,
  runtimeType,
  container,
  firewallRules = [],
  onFirewallRulesChange = () => {},
  configurationError,
}) => {
  const [activeTab, setActiveTab] = useState<string>("tunnels");
  const [newRulePort, setNewRulePort] = useState<string>("");
  const [newRuleAllowedIPs, setNewRuleAllowedIPs] = useState<string>("all");
  const [portConflictError, setPortConflictError] = useState<string | null>(
    null,
  );
  const [portRangeError, setPortRangeError] = useState<string | null>(null);
  const [firewallValidationError, setFirewallValidationError] = useState<
    string | null
  >(null);

  const ctaCount = services.filter((service) => service.isCTA).length;
  const ctaLimitReached = ctaCount >= MAX_CTA_COUNT;
  const displayedFirewallValidationError =
    configurationError || firewallValidationError;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Add a new service
  const addService = (isJupyter = false) => {
    // Set name based on runtime type and whether it's Jupyter
    let name = isJupyter
      ? runtimeType === "kubernetes"
        ? "k8s-dashboard"
        : "jupyter-notebook"
      : `tunnel-${services.length + 1}`;

    // Check if the name already exists and find the next available number
    if (services.find((service) => service.name === name)) {
      // Find the next available number
      let counter = services.length;
      let newName;
      do {
        counter++;
        newName = `tunnel-${counter}`;
      } while (services.find((service) => service.name === newName));
      name = newName;
    }

    // Use port 8001 for Kubernetes dashboard, 8888 for Jupyter
    const defaultPort = isJupyter
      ? runtimeType === "kubernetes"
        ? "8001"
        : "8888"
      : "";

    const newService: NetworkService = {
      id: `service-${Date.now()}`,
      name,
      port: defaultPort,
      isJupyter,
      containerImage: undefined,
    };

    const updatedServices = [...services, newService];
    const {
      services: checkedServices,
      firewallRules: checkedRules,
      hasConflict,
      hasRangeError,
    } = checkPortConflicts(updatedServices, firewallRules, newService.id);

    if (hasConflict) {
      setPortConflictError(
        "Warning: Port conflict detected between services. Please use different ports for your services.",
      );
    } else {
      setPortConflictError(null);
    }

    if (hasRangeError) {
      setPortRangeError(
        "Services can only use single port numbers, not port ranges.",
      );
    } else {
      setPortRangeError(null);
    }

    onServicesChange(checkedServices);
    onFirewallRulesChange(checkedRules);
  };

  // Remove a service
  const removeService = (id: string) => {
    const updatedServices = services.filter((service) => service.id !== id);
    const {
      services: checkedServices,
      firewallRules: checkedRules,
      hasConflict,
      hasRangeError,
    } = checkPortConflicts(updatedServices, firewallRules);

    if (hasConflict) {
      setPortConflictError(
        "Warning: Port conflict detected between services. Please use different ports for your services.",
      );
    } else {
      setPortConflictError(null);
    }

    if (hasRangeError) {
      setPortRangeError(
        "Services can only use single port numbers, not port ranges.",
      );
    } else {
      setPortRangeError(null);
    }

    onServicesChange(checkNameConflicts(checkedServices));
    onFirewallRulesChange(checkedRules);
  };

  const updateService = (id: string, updates: Partial<NetworkService>) => {
    const updatedServices = services.map((service) =>
      service.id === id ? { ...service, ...updates } : service,
    );

    const {
      services: checkedServices,
      firewallRules: checkedRules,
      hasConflict,
      hasRangeError,
    } = checkPortConflicts(updatedServices, firewallRules, id);

    if (hasConflict) {
      setPortConflictError(
        "Warning: Port conflict detected between services. Please use different ports for your services.",
      );
    } else {
      setPortConflictError(null);
    }

    if (hasRangeError) {
      setPortRangeError(
        "Services can only use single port numbers, not port ranges.",
      );
    } else {
      setPortRangeError(null);
    }

    onServicesChange(checkNameConflicts(checkedServices));
    onFirewallRulesChange(checkedRules);
  };

  const handleServiceNameChange = (serviceId: string, value: string) => {
    const nameErr = validateTunnelName(value);
    const isDuplicate =
      !nameErr &&
      services.some(
        (service) => service.id !== serviceId && service.name === value,
      );
    const finalNameErr =
      nameErr ||
      (isDuplicate
        ? `A secure link with the name "${value}" already exists`
        : "");
    updateService(serviceId, {
      name: value,
      ctaLabel: value,
      hasNameError: !!finalNameErr,
      nameError: finalNameErr || undefined,
    });
  };

  // Add a new firewall rule
  const addFirewallRule = () => {
    if (!newRulePort.trim()) {
      return;
    }

    if (!isValidPortFormat(newRulePort)) {
      if (newRulePort.includes("-")) {
        const parts = newRulePort.split("-").map((p) => p.trim());
        if (parts.length !== 2) {
          setFirewallValidationError(
            "Invalid range format. Use format: 8000-8100",
          );
          return;
        }

        const start = parseInt(parts[0]);
        const end = parseInt(parts[1]);

        if (isNaN(start) || isNaN(end)) {
          setFirewallValidationError("Ports must be valid numbers");
          return;
        }

        if (!isValidPortNumber(start) || !isValidPortNumber(end)) {
          setFirewallValidationError("Ports must be between 1 and 65535");
          return;
        }

        if (start >= end) {
          setFirewallValidationError("Start port must be less than end port");
          return;
        }
      } else {
        const port = parseInt(newRulePort);
        if (isNaN(port)) {
          setFirewallValidationError("Port must be a valid number");
          return;
        }
        if (!isValidPortNumber(port)) {
          setFirewallValidationError("Port must be between 1 and 65535");
          return;
        }
      }
    }

    const newRule: FirewallRule = {
      id: `rule-${Date.now()}`,
      ports: newRulePort.trim(),
      allowedIPs: newRuleAllowedIPs,
    };

    const updatedRules = [...firewallRules, newRule];
    const {
      services: checkedServices,
      firewallRules: checkedRules,
      hasConflict,
    } = checkPortConflicts(services, updatedRules, newRule.id);

    const newRuleWithValidation = checkedRules.find((r) => r.id === newRule.id);
    if (newRuleWithValidation?.hasPortConflict) {
      const conflictingRules = checkedRules.filter(
        (r) => r.id !== newRule.id && r.hasPortConflict,
      );
      const conflictingServices = checkedServices.filter(
        (s) => s.hasPortConflict,
      );

      if (conflictingRules.length > 0) {
        setFirewallValidationError(
          `Port${
            newRulePort.includes("-") ? " range" : ""
          } conflicts with existing rule${
            conflictingRules.length > 1 ? "s" : ""
          }: ${conflictingRules.map((r) => r.ports).join(", ")}`,
        );
        return;
      }

      if (conflictingServices.length > 0) {
        setFirewallValidationError(
          `Port${
            newRulePort.includes("-") ? " range" : ""
          } conflicts with service${
            conflictingServices.length > 1 ? "s" : ""
          }: ${conflictingServices
            .map((s) => `${s.name} (port ${s.port})`)
            .join(", ")}`,
        );
        return;
      }
    }

    setFirewallValidationError(null);
    onServicesChange(checkedServices);
    onFirewallRulesChange(checkedRules);
    setNewRulePort("");
    setNewRuleAllowedIPs("all");
  };

  // Remove a firewall rule
  const removeFirewallRule = (id: string) => {
    const updatedRules = firewallRules.filter((rule) => rule.id !== id);
    const {
      services: checkedServices,
      firewallRules: checkedRules,
      hasConflict,
      hasRangeError,
    } = checkPortConflicts(services, updatedRules);

    if (hasConflict) {
      setPortConflictError(
        "Warning: Port conflict detected. Please use different ports.",
      );
    } else {
      setPortConflictError(null);
    }

    if (hasRangeError) {
      setPortRangeError(
        "Services can only use single port numbers, not port ranges.",
      );
    } else {
      setPortRangeError(null);
    }

    onServicesChange(checkedServices);
    onFirewallRulesChange(checkedRules);
  };

  const jupyterService = services.find((service) => service.isJupyter);
  const additionalServices = services.filter((service) => !service.isJupyter);

  return (
    <div>
      <MuiTooltip
        title={
          !!container.verbBuild
            ? "Feature Containers don't support additional Secure Links or TCP/UDP Ports on creation."
            : ""
        }
        followCursor
        placement="top"
      >
        <div className="relative h-full">
          <div className={!!container.verbBuild ? "hidden" : ""}>
            <div className="mb-6">
              <h3 className="text-base font-medium text-white">
                Do you need to expose services to the internet?
              </h3>
              <p className="text-secondary text-sm">
                Use a Secure Link to protect a http service with login, or
                expose tcp/udp ports.
              </p>
            </div>
            <Tabs
              defaultValue="tunnels"
              className="mb-6"
              onValueChange={setActiveTab}
            >
              <TabsList className="grid w-full grid-cols-2" variant="solid">
                <TabsTrigger
                  value="tunnels"
                  className="flex items-center"
                  disabled={!!container.verbBuild}
                >
                  <LockKeyhole className="mr-2 h-4 w-4" /> Secure Link
                </TabsTrigger>
                <TabsTrigger
                  value="firewall"
                  className="flex items-center"
                  disabled={!!container.verbBuild}
                >
                  <Globe className="mr-2 h-4 w-4" /> TCP/UDP Ports
                </TabsTrigger>
              </TabsList>
              <TabsContent value="tunnels" className="pt-4 space-y-6">
                {runtimeType === "kubernetes" && <MicroK8sNodePortNotice />}
                {jupyterService && (
                  <FlatCard className="relative">
                    <div className="">
                      <div className="flex justify-between items-center">
                        <h2 className="text-base flex items-center flex-1">
                          <Server className="mr-2 h-5 w-5 text-highlight" />
                          {runtimeType === "kubernetes"
                            ? "Kubernetes Dashboard"
                            : runtimeType === "VM"
                              ? "Jupyter Notebook"
                              : jupyterService.name}
                        </h2>
                        <div className="flex items-center justify-end gap-4 flex-1">
                          <span>Port: {jupyterService.port}</span>
                          <MuiTooltip
                            title="This secure link is controlled by Software configuration and cannot be removed from Network."
                            placement="top"
                          >
                            <span className="inline-flex cursor-help text-secondary">
                              <Info className="h-4 w-4" />
                              <span className="sr-only">
                                Controlled by Software configuration
                              </span>
                            </span>
                          </MuiTooltip>
                        </div>
                      </div>
                    </div>
                  </FlatCard>
                )}
                {additionalServices.length === 0 && !jupyterService ? (
                  <div className="text-center py-8 border-2 border-dashed border-zinc-800 rounded-lg">
                    <LockKeyhole className="mx-auto h-12 w-12 text-gray-400 mb-3" />
                    <h3 className="text-lg font-medium text-primary mb-1">
                      No additional secure links configured
                    </h3>
                    <p className="mb-4 text-sm text-secondary">
                      Add secure links to expose http services with login
                    </p>
                    <Button
                      onClick={() => addService()}
                      disabled={!!container.verbBuild}
                    >
                      <Plus className="mr-2 h-4 w-4" /> Add Secure Link
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {additionalServices.map((service) => {
                      const isComposeManaged = !!service.isEnvironmentManaged;

                      return (
                        <FlatCard key={service.id} className="relative">
                          <div className="pb-2">
                            <div className="flex justify-between items-center">
                              <h2 className="text-base flex items-center">
                                <Server className="mr-2 h-5 w-5 text-highlight" />
                                {service.name}
                                {service.hasNameConflict && (
                                  <span className="ml-2 text-xs bg-red-500 text-white px-2 py-1 rounded-full">
                                    Name Conflict
                                  </span>
                                )}
                                {service.hasPortConflict && (
                                  <span className="ml-2 text-xs bg-red-500 text-white px-2 py-1 rounded-full">
                                    Port Conflict
                                  </span>
                                )}
                                {service.hasPortRangeError && (
                                  <span className="ml-2 text-xs bg-red-500 text-white px-2 py-1 rounded-full">
                                    Invalid Port
                                  </span>
                                )}
                                {service.hasInvalidPortError && (
                                  <span className="ml-2 text-xs bg-red-500 text-white px-2 py-1 rounded-full">
                                    Invalid Port Number
                                  </span>
                                )}
                              </h2>
                              <div className="flex items-center">
                                {isComposeManaged && (
                                  <div className="flex items-center gap-2">
                                    <MuiTooltip
                                      placement="top"
                                      title="This secure link was generated from the Docker Compose file. You can edit or remove it here."
                                    >
                                      <span className="inline-flex cursor-help text-secondary">
                                        <Info className="h-4 w-4" />
                                        <span className="sr-only">
                                          Generated from Docker Compose
                                        </span>
                                      </span>
                                    </MuiTooltip>
                                  </div>
                                )}
                                <Button
                                  variant="ghost"
                                  onClick={() => removeService(service.id)}
                                  disabled={!!container.verbBuild}
                                  aria-label={`Remove secure link ${service.name}`}
                                >
                                  <Trash2 className="h-4 w-4 text-red-500" />
                                </Button>
                              </div>
                            </div>
                          </div>
                          <div className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <label
                                  htmlFor={`service-name-${service.id}`}
                                  className="text-sm"
                                >
                                  Secure Link Name
                                </label>
                                <Input
                                  id={`service-name-${service.id}`}
                                  value={service.name}
                                  hasError={
                                    service.hasNameError ||
                                    service.hasNameConflict
                                  }
                                  onChange={(e) =>
                                    handleServiceNameChange(
                                      service.id,
                                      e.target.value,
                                    )
                                  }
                                />
                                {service.hasNameError && (
                                  <p className="text-xs text-red-400">
                                    {service.nameError}
                                  </p>
                                )}
                                {!service.hasNameError &&
                                  service.hasNameConflict && (
                                    <p className="text-xs text-yellow-700">
                                      This name conflicts with another secure
                                      link
                                    </p>
                                  )}
                              </div>
                              <div className="space-y-2">
                                <label htmlFor={`service-port-${service.id}`}>
                                  Port
                                </label>
                                <Input
                                  id={`service-port-${service.id}`}
                                  value={service.port}
                                  hasError={
                                    service.hasPortConflict ||
                                    service.hasPortRangeError ||
                                    service.hasInvalidPortError
                                  }
                                  onChange={(e) =>
                                    updateService(service.id, {
                                      port: e.target.value,
                                    })
                                  }
                                  placeholder="e.g., 8080"
                                />
                                {service.port && service.hasPortConflict && (
                                  <p className="text-xs text-yellow-700">
                                    This port conflicts with another service
                                  </p>
                                )}
                                {service.port && service.hasPortRangeError && (
                                  <p className="text-xs text-yellow-700">
                                    Services can only use single port numbers,
                                    not ranges
                                  </p>
                                )}
                                {service.port &&
                                  service.hasInvalidPortError && (
                                    <p className="text-xs text-yellow-700">
                                      Please enter a valid port number between 1
                                      and 65535
                                    </p>
                                  )}
                              </div>
                            </div>
                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <Switch
                                    checked={!!service.isCTA}
                                    onCheckedChange={(checked) =>
                                      updateService(service.id, {
                                        isCTA: checked,
                                        ctaLabel: service.name,
                                      })
                                    }
                                    disabled={
                                      !!container.verbBuild ||
                                      (ctaLimitReached && !service.isCTA)
                                    }
                                  />
                                  <MuiTooltip
                                    title="Promote this Secure Link as a one-click button, similar to the Jupyter Open Notebook experience."
                                    placement="top"
                                  >
                                    <span className="text-sm cursor-help">
                                      Set as CTA
                                    </span>
                                  </MuiTooltip>
                                </div>
                                {ctaLimitReached && !service.isCTA && (
                                  <span className="text-xs text-secondary">
                                    Max {MAX_CTA_COUNT} CTAs
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </FlatCard>
                      );
                    })}
                    <div className="flex justify-center items-center mb-4">
                      <Button
                        onClick={() => addService()}
                        variant="primary"
                        className="flex items-center"
                        disabled={!!container.verbBuild}
                      >
                        <Plus className="mr-2 h-4 w-4" /> Add Secure Link
                      </Button>
                    </div>
                  </div>
                )}
              </TabsContent>
              <TabsContent value="firewall" className="pt-4">
                <FlatCard>
                  <div className="pb-2">
                    <p className="text-sm text-primary">
                      Port(s) (range allowed)
                    </p>
                  </div>
                  <div className="flex flex-col w-full">
                    <div className="flex items-start gap-2 max-w-[550px]">
                      <div className="flex flex-col w-full items-start">
                        <Input
                          placeholder="Port or port range (e.g., 8000-8100)"
                          className="flex-1"
                          value={newRulePort}
                          onChange={(e) => setNewRulePort(e.target.value)}
                        />
                      </div>
                      <div className="flex items-center">
                        <Select
                          value={newRuleAllowedIPs}
                          onValueChange={(value) => setNewRuleAllowedIPs(value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Allow All IPs" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Allow All IPs</SelectItem>
                            <SelectItem value="user-ip">
                              {"Allow Only User's IP"}
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Button
                        variant="primary"
                        onClick={addFirewallRule}
                        disabled={!!container.verbBuild}
                      >
                        <Plus className="h-4 w-4" /> Add Rule
                      </Button>
                    </div>
                    {displayedFirewallValidationError && (
                      <p className="text-xs text-red-500 mt-2">
                        {displayedFirewallValidationError}
                      </p>
                    )}
                    {portConflictError && (
                      <p className="mt-2 text-xs text-red-500">
                        {portConflictError}
                      </p>
                    )}
                    {portRangeError && (
                      <p className="mt-2 text-xs text-red-500">
                        {portRangeError}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col w-full mt-2">
                    <div className="flex items-center w-full">
                      <div className="flex flex-col w-full space-y-4">
                        <div className="flex flex-col w-full pt-4">
                          <div className="space-y-4">
                            {firewallRules.map((rule) => (
                              <div
                                key={rule.id}
                                className="flex items-center gap-2 p-3 rounded-md border border-zinc-800"
                              >
                                <div className="flex-1 flex items-center">
                                  <Server className="mr-2 h-5 w-5 text-highlight" />
                                  <p className="text-sm font-medium">
                                    {rule.ports}
                                    {rule.allowedIPs === "0.0.0.0/0" && (
                                      <span className="inline-flex items-center gap-x-1 whitespace-nowrap rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset bg-gray-50 text-gray-900 ring-gray-500/30 dark:bg-gray-400/10 dark:text-gray-400 dark:ring-gray-400/20 ml-2">
                                        Allow All IPs
                                      </span>
                                    )}
                                    {rule.allowedIPs === "user-ip" && (
                                      <span className="inline-flex items-center gap-x-1 whitespace-nowrap rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset bg-gray-50 text-gray-900 ring-gray-500/30 dark:bg-gray-400/10 dark:text-gray-400 dark:ring-gray-400/20 ml-2">
                                        {"Allow Only User's IP"}
                                      </span>
                                    )}
                                    {rule.hasPortConflict && (
                                      <span className="ml-2 text-xs bg-yellow-200 text-yellow-800 px-2 py-0.5 rounded-full">
                                        Conflict
                                      </span>
                                    )}
                                    {rule.hasInvalidFormatError && (
                                      <span className="ml-2 text-xs bg-red-500 text-white px-2 py-0.5 rounded-full">
                                        Invalid Format
                                      </span>
                                    )}
                                  </p>
                                </div>
                                <Button
                                  variant="ghost"
                                  onClick={() => removeFirewallRule(rule.id)}
                                  disabled={!!container.verbBuild}
                                >
                                  <Trash2 className="h-4 w-4 text-red-500" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </FlatCard>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </MuiTooltip>
    </div>
  );
};

export default NetworkAccessConfiguration;
